import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, where, limit, startAfter, getDoc, doc } from 'firebase/firestore';
import CoursesTray from '../courses/CoursesTray';
import EnrolledCourses from '../courses/EnrolledCourses';
import QuizSection from './QuizSection';
import Announcements from './Announcements';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../common/LoadingIndicator';
import CourseUserAnalytics from '../courses/CourseUserAnalytics';
import { useNavigate } from 'react-router-dom';
import CognitionLab from './CognitionLab';

function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    enrolledCourses: [],
    announcements: [],
    lastAnnouncementDoc: null,
    hasMoreAnnouncements: true
  });

  // Cache key for local storage
  const CACHE_KEY = `dashboard_${user?.uid}`;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const fetchFromCache = useCallback(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data: cachedData, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        setData(prevData => ({ ...prevData, ...cachedData }));
        return true;
      }
    }
    return false;
  }, [CACHE_KEY]);

  const updateCache = useCallback((newData) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data: newData,
      timestamp: Date.now()
    }));
  }, [CACHE_KEY]);

  // Parallel data fetching
  const fetchDashboardData = useCallback(async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);

      // Check cache first
      if (fetchFromCache()) {
        setLoading(false);
        return;
      }

      // Parallel fetching of courses and announcements
      const [enrolledCoursesData, announcementsData] = await Promise.all([
        fetchEnrolledCourses(),
        fetchAnnouncements()
      ]);

      const newData = {
        enrolledCourses: enrolledCoursesData,
        announcements: announcementsData.announcements,
        lastAnnouncementDoc: announcementsData.lastDoc,
        hasMoreAnnouncements: announcementsData.hasMore
      };

      setData(newData);
      updateCache(newData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, navigate, fetchFromCache, updateCache]);

  const fetchEnrolledCourses = async () => {
    const enrolledRef = collection(db, 'users', user.uid, 'courseProgress');
    const enrolledSnapshot = await getDocs(enrolledRef);
    return enrolledSnapshot.docs.map(doc => ({
      courseId: doc.id,
      ...doc.data()
    }));
  };

  const fetchAnnouncements = async (lastDoc = null) => {
    const ANNOUNCEMENTS_PER_PAGE = 5;
    let announcementsQuery = query(
      collection(db, 'announcements'),
      where('active', '==', true),
      limit(ANNOUNCEMENTS_PER_PAGE)
    );

    if (lastDoc) {
      announcementsQuery = query(
        announcementsQuery,
        startAfter(lastDoc)
      );
    }

    const snapshot = await getDocs(announcementsQuery);
    return {
      announcements: snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })),
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      hasMore: snapshot.docs.length === ANNOUNCEMENTS_PER_PAGE
    };
  };

  // Load more announcements
  const loadMoreAnnouncements = async () => {
    if (!data.hasMoreAnnouncements) return;

    const moreAnnouncements = await fetchAnnouncements(data.lastAnnouncementDoc);
    setData(prevData => ({
      ...prevData,
      announcements: [...prevData.announcements, ...moreAnnouncements.announcements],
      lastAnnouncementDoc: moreAnnouncements.lastDoc,
      hasMoreAnnouncements: moreAnnouncements.hasMore
    }));
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  if (loading) return <LoadingIndicator />;

  return (
    <div className="min-h-screen bg-gray-100">     
      <div className="w-full">
        {/* Main Content */}
        <main className="w-full px-2 py-2 sm:px-4 sm:py-4" style={{ contain: 'layout' }}>
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 gap-3 sm:gap-6" style={{ contain: 'layout' }}>
              <div className="w-full bg-white rounded-lg shadow-sm" style={{ contain: 'layout style paint' }}>
                <Announcements announcements={data.announcements} />
              </div>
              
              <div className="w-full bg-white rounded-lg shadow-sm" style={{ contain: 'layout style paint' }}>
                <CoursesTray />
              </div>
              
              <div className="w-full bg-white rounded-lg shadow-sm" style={{ contain: 'layout style paint' }}>
                <EnrolledCourses enrolledCourses={data.enrolledCourses} />
              </div>
              
              <div className="w-full bg-white rounded-lg shadow-sm" style={{ contain: 'layout style paint' }}>
                <CognitionLab />
              </div>
              
              <div className="w-full bg-white rounded-lg shadow-sm" style={{ contain: 'layout style paint' }}>
                <QuizSection />
              </div>
              
              <div className="w-full bg-white rounded-lg shadow-sm" style={{ contain: 'layout style paint' }}>
                <CourseUserAnalytics />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Dashboard;